import React, { useEffect, useState } from "react";
import CalorieComparison from "../Weekly/CalorieComparison";
import WaterIntakeChart from "../Weekly/WaterIntakeChart";
import SleepChart from "../Weekly/SleepChart";
import DailyStepsChart from "../Weekly/DailyStepsChart";
import ActivityTrendChart from "../Weekly/ActivityTrendChart";
import BloodPressureChart from "../Weekly/BloodPressureChart";
import FortnightlyLogConsistency from "./FortnightlyLogConsistency";
import FortnightlyStreakCard from "./FortnightlyStreakCard";
import { getInsightsFortnightlyData } from "../../../../../services/clientDetails";

// ── API response types ────────────────────────────────────────────────────
interface DayStatus {
  date: string;
  status: string; // "complete" | "partial" | "future" | "none"
}

interface WeekConsistencyBlock {
  overall: number;
  meals: number;
  water: number;
  sleep: number;
  gut: number;
  last_week_overall: number;
  change_points: number;
}

interface WeekCalorieBlock {
  daily: { date: string; calories_intake: number; calories_burned: number }[];
  this_week_total_intake: number;
  last_week_total_intake: number;
  change_percent: number | null;
}

interface WeekWaterBlock {
  daily: {
    date: string;
    water_intake_ml: number;
    goal_liters: number;
    percent_of_goal: number;
  }[];
  total_ml: number;
  avg_liters: number;
  change_percent: number | null;
}

interface WeekSleepBlock {
  this_week: { date: string; sleep_hours: number; mood: string | null }[];
  last_week: { date: string; sleep_hours: number; mood: string | null }[];
  avg_nap_hours: number;
}

interface WeekActivityBlock {
  daily_steps: { date: string; steps: number; calories_burned: number; distance_km: number }[];
  total_steps: number;
  activity_trend: { date: string; minutes: number }[];
  avg_duration_minutes: number;
}

interface FortnightlyInsightsData {
  week1_start: string;
  week1_end: string;
  week2_start: string;
  week2_end: string;
  period_start: string;
  period_end: string;
  log_consistency: { week1: WeekConsistencyBlock; week2: WeekConsistencyBlock };
  calorie_comparison: { week1: WeekCalorieBlock; week2: WeekCalorieBlock };
  water: { week1: WeekWaterBlock; week2: WeekWaterBlock };
  sleep: { week1: WeekSleepBlock; week2: WeekSleepBlock };
  activity: { week1: WeekActivityBlock; week2: WeekActivityBlock };
  blood_pressure: {
    week1: { date: string; systolic: number | null; diastolic: number | null }[];
    week2: { date: string; systolic: number | null; diastolic: number | null }[];
  };
  streak: {
    current_streak: number;
    longest_streak: number;
    days_missed: number;
    days: DayStatus[];
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const dayLabel = (dateStr: string): string => {
  const d = new Date(dateStr);
  return DAY_LABELS[d.getDay()];
};

const formatDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const mapStatus = (val: string): "none" | "partial" | "logged" => {
  if (val === "complete") return "logged";
  if (val === "partial") return "partial";
  return "none";
};

const formatK = (v: number): string => {
  if (v >= 1000) {
    const val = v / 1000;
    return `${val.toFixed(val % 1 === 0 ? 0 : 1)}k`;
  }
  return `${v}`;
};

interface FortnightlyInsightsViewProps {
  userId: number;
  week1Start: Date;
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
}

/** Card wrapping two bare charts side-by-side under a single title + optional right slot. */
const PairCard: React.FC<{
  title: string;
  right?: React.ReactNode;
  left: React.ReactNode;
  rightChart: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, right, left, rightChart, footer }) => (
  <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
        {title}
      </span>
      {right}
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="flex flex-col">
        {left}
        <span className="text-center text-[11px] 3xl:text-[12px] font-semibold tracking-widest text-[#8D8D8D] font-inter mt-2 uppercase">
          Week 1
        </span>
      </div>
      <div className="flex flex-col">
        {rightChart}
        <span className="text-center text-[11px] 3xl:text-[12px] font-semibold tracking-widest text-[#8D8D8D] font-inter mt-2 uppercase">
          Week 2
        </span>
      </div>
    </div>
    {footer}
  </div>
);

const Legend: React.FC<{ items: { color: string; label: string }[] }> = ({ items }) => (
  <div className="flex items-center gap-3">
    {items.map((i) => (
      <div key={i.label} className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full inline-block" style={{ background: i.color }} />
        <span className="text-[11px] 3xl:text-[12px] text-[#1F1F1F] font-inter">{i.label}</span>
      </div>
    ))}
  </div>
);

const FortnightlyInsightsView: React.FC<FortnightlyInsightsViewProps> = ({
  userId,
  week1Start,
  refreshKey = 0,
  onLoadingChange,
}) => {
  const [data, setData] = useState<FortnightlyInsightsData | null>(null);
  const key = `${refreshKey}-${week1Start.toISOString()}`;

  useEffect(() => {
    const fetchInsights = async () => {
      setData(null);
      onLoadingChange?.(true);
      try {
        const json: FortnightlyInsightsData = await getInsightsFortnightlyData(
          userId,
          formatDate(week1Start),
        );
        setData(json);
      } catch (err) {
        console.error("FortnightlyInsightsView fetch error:", err);
        setData(null);
      } finally {
        onLoadingChange?.(false);
      }
    };
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, week1Start, refreshKey]);

  // ── Derived per-chart data ─────────────────────────────────────────────
  const lc = data?.log_consistency;
  const st = data?.streak;
  const cc = data?.calorie_comparison;
  const wt = data?.water;
  const sl = data?.sleep;
  const act = data?.activity;
  const bp = data?.blood_pressure;

  const mapCalorieDaily = (rows?: WeekCalorieBlock["daily"]) =>
    rows?.map((d) => ({
      day: dayLabel(d.date),
      intake: d.calories_intake,
      burned: d.calories_burned,
    }));

  const mapWaterDaily = (rows?: WeekWaterBlock["daily"]) =>
    rows?.map((d) => ({
      day: dayLabel(d.date),
      actual: parseFloat((d.water_intake_ml / 1000).toFixed(2)),
      target: d.goal_liters,
    }));

  const mapStepsDaily = (rows?: WeekActivityBlock["daily_steps"]) =>
    rows?.map((d) => ({ day: dayLabel(d.date), steps: d.steps }));

  const mapActivityTrend = (rows?: WeekActivityBlock["activity_trend"]) =>
    rows?.map((d) => ({ day: dayLabel(d.date), minutes: d.minutes }));

  const mapBloodPressure = (rows?: { date: string; systolic: number | null; diastolic: number | null }[]) =>
    rows?.map((d) => ({ day: dayLabel(d.date), systolic: d.systolic, diastolic: d.diastolic }));

  const calorieW1 = mapCalorieDaily(cc?.week1.daily);
  const calorieW2 = mapCalorieDaily(cc?.week2.daily);

  const waterW1 = mapWaterDaily(wt?.week1.daily);
  const waterW2 = mapWaterDaily(wt?.week2.daily);

  const stepsW1 = mapStepsDaily(act?.week1.daily_steps);
  const stepsW2 = mapStepsDaily(act?.week2.daily_steps);

  const activityW1 = mapActivityTrend(act?.week1.activity_trend);
  const activityW2 = mapActivityTrend(act?.week2.activity_trend);

  const bpW1 = mapBloodPressure(bp?.week1);
  const bpW2 = mapBloodPressure(bp?.week2);

  // Sleep chart shows a single 7-day view comparing week2 (this week) to week1 (last week).
  // The API's sleep.week2.last_week is exactly week1's days, so use that pairing.
  const sleepData = sl
    ? sl.week2.this_week.map((tw, i) => ({
        day: dayLabel(tw.date),
        thisWeek: tw.sleep_hours ? parseFloat(tw.sleep_hours.toFixed(1)) : null,
        lastWeek: sl.week2.last_week[i]?.sleep_hours
          ? parseFloat(sl.week2.last_week[i].sleep_hours.toFixed(1))
          : null,
      }))
    : undefined;

  const toWeekConsistency = (w?: WeekConsistencyBlock) =>
    w
      ? {
          score: Math.round(w.overall),
          meals: Math.round(w.meals),
          water: Math.round(w.water),
          sleep: Math.round(w.sleep),
          gut: Math.round(w.gut),
        }
      : undefined;

  const logConsW1 = toWeekConsistency(lc?.week1);
  const logConsW2 = toWeekConsistency(lc?.week2);

  const streakDayLogs = st
    ? (st.days.map((d) => mapStatus(d.status)) as ("none" | "partial" | "logged")[])
    : undefined;

  const w1AvgL = wt?.week1.avg_liters;
  const w2AvgL = wt?.week2.avg_liters;
  const w1TotalSteps = act?.week1.total_steps ?? 0;
  const w2TotalSteps = act?.week2.total_steps ?? 0;
  const w1AvgHours =
    act?.week1.avg_duration_minutes != null
      ? parseFloat((act.week1.avg_duration_minutes / 60).toFixed(1))
      : undefined;
  const w2AvgHours =
    act?.week2.avg_duration_minutes != null
      ? parseFloat((act.week2.avg_duration_minutes / 60).toFixed(1))
      : undefined;

  return (
    <>
      {/* Row 1: Log Consistency | Streak */}
      <div key={`f-r1-${key}`} className="grid grid-cols-5 gap-4 px-6 mb-4 items-stretch">
        <div className="col-span-2 h-full">
          <FortnightlyLogConsistency week1={logConsW1} week2={logConsW2} />
        </div>
        <div className="col-span-3 h-full">
          <FortnightlyStreakCard
            currentStreak={st?.current_streak}
            daysMissed={st?.days_missed}
            longestStreak={st?.longest_streak}
            week1Start={week1Start}
            dayLogs={streakDayLogs}
          />
        </div>
      </div>

      {/* Row 2: Calorie Comparison */}
      <div key={`f-r2-${key}`} className="px-6 mb-4">
        <PairCard
          title="Calorie Comparison"
          right={<Legend items={[{ color: "#FF9130", label: "Calorie intake" }, { color: "#FFE448", label: "Calorie burned" }]} />}
          left={<CalorieComparison bare data={calorieW1} />}
          rightChart={<CalorieComparison bare data={calorieW2} />}
        />
      </div>

      {/* Row 3: Water Intake */}
      <div key={`f-r3-${key}`} className="px-6 mb-4">
        <PairCard
          title="Water Intake"
          right={
            <div className="flex items-center gap-4 text-[11px] 3xl:text-[12px] text-[#8D8D8D] font-inter">
              <span>
                Week 1 avg :{" "}
                <span className="font-semibold text-[#1F1F1F]">
                  {w1AvgL != null ? `${w1AvgL} L` : "—"}
                </span>
              </span>
              <span>
                Week 2 avg :{" "}
                <span className="font-semibold text-[#1F1F1F]">
                  {w2AvgL != null ? `${w2AvgL} L` : "—"}
                </span>
              </span>
            </div>
          }
          left={<WaterIntakeChart bare data={waterW1} />}
          rightChart={<WaterIntakeChart bare data={waterW2} />}
        />
      </div>

      {/* Row 4: Sleep (single) | Daily Steps (paired) */}
      <div key={`f-r4-${key}`} className="grid grid-cols-5 gap-4 px-6 mb-4 items-stretch">
        <div className="col-span-2 h-full">
          <SleepChart
            data={sleepData}
            avgNapHours={sl?.week2.avg_nap_hours}
            labels={{ thisWeek: "Week 2", lastWeek: "Week 1" }}
            reverseLegend
          />
        </div>
        <div className="col-span-3 h-full">
          <PairCard
            title="Daily Steps"
            right={
              <div className="flex items-center gap-4 text-[11px] 3xl:text-[12px] text-[#8D8D8D] font-inter">
                <span>
                  Week 1 total :{" "}
                  <span className="font-semibold text-[#1F1F1F]">{formatK(w1TotalSteps)}</span>
                </span>
                <span>
                  Week 2 total :{" "}
                  <span className="font-semibold text-[#1F1F1F]">{formatK(w2TotalSteps)}</span>
                </span>
              </div>
            }
            left={<DailyStepsChart bare data={stepsW1} />}
            rightChart={<DailyStepsChart bare data={stepsW2} />}
          />
        </div>
      </div>

      {/* Row 5: Activity Trend | Blood Pressure — both paired */}
      <div key={`f-r5-${key}`} className="grid grid-cols-2 gap-4 px-6 mb-4 items-stretch">
        <div className="h-full">
          <PairCard
            title="Activity Trend"
            right={
              <div className="flex items-center gap-4 text-[11px] 3xl:text-[12px] text-[#8D8D8D] font-inter">
                <span>
                  Week 1 avg :{" "}
                  <span className="font-semibold text-[#1F1F1F]">
                    {w1AvgHours != null ? `${w1AvgHours} hour` : "—"}
                  </span>
                </span>
                <span>
                  Week 2 avg :{" "}
                  <span className="font-semibold text-[#1F1F1F]">
                    {w2AvgHours != null ? `${w2AvgHours} hour` : "—"}
                  </span>
                </span>
              </div>
            }
            left={<ActivityTrendChart bare data={activityW1} />}
            rightChart={<ActivityTrendChart bare data={activityW2} />}
          />
        </div>
        <div className="h-full">
          <PairCard
            title="Blood Pressure (mmHg)"
            right={<Legend items={[{ color: "#22C55E", label: "Systolic" }, { color: "#FF9130", label: "Diastolic" }]} />}
            left={<BloodPressureChart bare data={bpW1} />}
            rightChart={<BloodPressureChart bare data={bpW2} />}
          />
        </div>
      </div>
    </>
  );
};

export default FortnightlyInsightsView;
